import { Box } from "@mui-wrapper";
import { useState } from "react";
import {
  ImpersonationTarget,
  useImpersonationTrackEvent,
  ImpersonationLaunchMode,
  formatImpersonationTarget,
  impersonationLaunchModeDescription,
  getImpersonationTargetLabel,
} from "@components/impersonation/impersonation-helper";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { RequirePermission } from "@components/common/require-permission";
import { LinkWithQRCodeDialog } from "@components/common/link-with-qrcode-dialog";
import { ActionButtonSx } from "@styles/common-styles";
import { upperFirst } from "es-toolkit/string";
import { Button, Menu, MenuItem } from "@flat-ui";

interface Props {
  /**
   * Different types to log in
   * "workspaceViewer" - log in as workspace viewer
   * "user" - log in as a specific user
   */
  type: ImpersonationTarget;

  /** ID of the subject to log in as */
  subjectId: string;
}

/**
 * Component to handle impersonation functionality in workspace profile
 * Letting user to log in as a member of our applications
 */
export function Impersonation({ type, subjectId }: Props): React.JSX.Element {
  const { handleErrorWithToast } = useErrorHandler();
  const { trackImpersonation } = useImpersonationTrackEvent(type);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isOpen = Boolean(anchorEl);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [accessLink, setAccessLink] = useState<string>("");

  async function onImpersonationRequest(
    loginMode: ImpersonationLaunchMode
  ): Promise<void> {
    setIsLoading(true);
    await trackImpersonation(loginMode);
    let newAccessLink: string;
    try {
      newAccessLink = await getImpersonationTargetLabel(type, subjectId);

      setAnchorEl(null);
      setAccessLink(newAccessLink || "");

      if (loginMode === "currentTab" && newAccessLink) {
        window.open(newAccessLink, "_self");
      } else if (loginMode === "copyLink") {
        setIsDialogOpen(true);
      }
    } catch (error) {
      handleErrorWithToast({
        id: `login-as-${Date.now()}`,
        title:
          type === "workspaceViewer"
            ? "Could not retrieve the workspace viewer access link"
            : "Could not retrieve the user login access link",
        error,
      });
    } finally {
      setIsLoading(false);
    }
  }

  const impersonationButton = (
    <Button
      onClick={({ currentTarget }) => setAnchorEl(currentTarget)}
      isLoading={isLoading}
      sx={{ ...ActionButtonSx }}
      variant="secondary"
    >
      {upperFirst(formatImpersonationTarget(type))}
    </Button>
  );

  return (
    <Box>
      <RequirePermission
        permission={
          type === "workspaceViewer"
            ? "workspace.impersonate"
            : "member.impersonate"
        }
      >
        {impersonationButton}
      </RequirePermission>

      <Menu anchorEl={anchorEl} open={isOpen} onClose={() => setAnchorEl(null)}>
        <MenuItem
          key={impersonationLaunchModeDescription.currentTab}
          label={impersonationLaunchModeDescription.currentTab}
          onClick={() => onImpersonationRequest("currentTab")}
        />

        <MenuItem
          key={impersonationLaunchModeDescription.copyLink}
          label={impersonationLaunchModeDescription.copyLink}
          onClick={() => onImpersonationRequest("copyLink")}
        />
      </Menu>

      {isDialogOpen && (
        <LinkWithQRCodeDialog
          title={`Scan this code to ${formatImpersonationTarget(type)}`}
          onDialogClose={() => setIsDialogOpen(false)}
          link={accessLink}
          location={formatImpersonationTarget(type)}
        />
      )}
    </Box>
  );
}
